type ValidationRuleT = 'required' | 'mustBeText' | 'mustBeNumber' | 'mustBeDate' | 'tooLong';

interface ValidationPartI {
  field: string;
  rule: ValidationRuleT;
  max: string;
}

// class-validator default messages, matched as a whole part.
const VALIDATION_PATTERNS: ReadonlyArray<{ rule: ValidationRuleT; pattern: RegExp }> = [
  { rule: 'required', pattern: /^([A-Za-z][A-Za-z0-9]*) should not be empty$/ },
  { rule: 'mustBeText', pattern: /^([A-Za-z][A-Za-z0-9]*) must be a string$/ },
  {
    rule: 'mustBeNumber',
    pattern: /^([A-Za-z][A-Za-z0-9]*) must be a number conforming to the specified constraints$/,
  },
  { rule: 'mustBeDate', pattern: /^([A-Za-z][A-Za-z0-9]*) must be a valid ISO 8601 date string$/ },
  { rule: 'tooLong', pattern: /^([A-Za-z][A-Za-z0-9]*) must be shorter than or equal to (\d+) characters$/ },
];

function parsePart(part: string): ValidationPartI | null {
  for (const { rule, pattern } of VALIDATION_PATTERNS) {
    const match = pattern.exec(part);
    if (match) {
      return { field: match[1], rule, max: match[2] ?? '' };
    }
  }
  return null;
}

/** 'regionActivatedDate' -> 'Region activated date'. */
function camelToLabel(field: string): string {
  const words = field.replace(/([A-Z])/g, ' $1').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Turn the API's joined class-validator message (parts separated by '; ',
 * e.g. `regionName must be a string; regionName should not be empty`) into
 * one friendly sentence per field. All prose arrives via `copy` (templates use
 * `{field}` and `{max}`); this file holds none. A message with no recognised
 * part is returned unchanged, so custom server copy passes through intact.
 */
export function humanizeValidationMessage(
  message: string,
  copy: {
    readonly labels: Readonly<Record<string, string>>;
    readonly required: string;
    readonly mustBeText: string;
    readonly mustBeNumber: string;
    readonly mustBeDate: string;
    readonly tooLong: string;
  },
): string {
  const parts = message.split('; ').map(part => ({ raw: part, parsed: parsePart(part) }));
  if (parts.every(part => part.parsed === null)) {
    return message;
  }

  // One rule per field: 'required' wins, otherwise the first recognised one.
  const chosen = new Map<string, ValidationPartI>();
  for (const { parsed } of parts) {
    if (!parsed) continue;
    const current = chosen.get(parsed.field);
    if (!current || (parsed.rule === 'required' && current.rule !== 'required')) {
      chosen.set(parsed.field, parsed);
    }
  }

  const emitted = new Set<string>();
  const pieces: string[] = [];
  for (const { raw, parsed } of parts) {
    if (!parsed) {
      pieces.push(raw);
    } else if (!emitted.has(parsed.field)) {
      emitted.add(parsed.field);
      const { field, rule, max } = chosen.get(parsed.field) as ValidationPartI;
      pieces.push(
        copy[rule].replace('{field}', () => copy.labels[field] ?? camelToLabel(field)).replace('{max}', () => max),
      );
    }
  }
  return pieces.join(' ');
}
